import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  FaCheck,
  FaColumns,
  FaCopy,
  FaFilter,
  FaFolderOpen,
  FaPrint,
  FaSearch,
  FaUsers,
} from "react-icons/fa";
import Spinner from "../components/Spinner";
import ErrorPage from "../components/ErrorPage";
import {
  LdsOverviewHero,
  LdsOverviewPage,
} from "../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";
import http from "../api/apiClient";
import { printPage } from "../utils/printPage";
import { getChildCount } from "./familyListUtils";
import { familySituationLabel } from "../utils/familySituation";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import {
  buildFamilyClipboardText,
  filterFamilyReportRows,
  formatFamilyChildren,
  type FamilyReportRow,
  type FamilyStatusFilter,
} from "./reports/familyReportUtils";

type ColumnKey =
  | "familyName"
  | "childCount"
  | "children"
  | "active"
  | "familySituation"
  | "parent1"
  | "parent2";

const DEFAULT_COLUMNS: ColumnKey[] = ["familyName", "childCount", "active"];

const parentName = (
  family: FamilyReportRow,
  parent: 1 | 2,
): string =>
  [family[`parent${parent}FirstName`], family[`parent${parent}LastName`]]
    .filter(Boolean)
    .join(" ")
    .trim();

const copyText = async (text: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("Clipboard copy was rejected");
};

export default function ReportsFamilies(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [families, setFamilies] = useState<FamilyReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message?: string; status?: number } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<FamilyStatusFilter>("all");
  const [sortEmptyFirst, setSortEmptyFirst] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<ColumnKey[]>(DEFAULT_COLUMNS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle");
  const controlsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    http.get("/reports/families")
      .then(({ data }) => {
        if (!cancelled) setFamilies(Array.isArray(data) ? data : []);
      })
      .catch((caught) => {
        if (!cancelled) setError(mapHttpErrorToMessage(caught, t));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    const closeMenus = (event: MouseEvent) => {
      if (event.target instanceof Node && controlsRef.current?.contains(event.target)) return;
      setFilterOpen(false);
      setColumnsOpen(false);
    };
    document.addEventListener("mousedown", closeMenus);
    return () => document.removeEventListener("mousedown", closeMenus);
  }, []);

  const columns = useMemo(
    () => [
      {
        key: "familyName" as const,
        label: t("reports.families.columns.family_name", { defaultValue: "Family name" }),
        value: (family: FamilyReportRow) => family.familyName || "—",
      },
      {
        key: "childCount" as const,
        label: t("reports.families.columns.child_count", { defaultValue: "Number of children" }),
        value: (family: FamilyReportRow) => String(getChildCount(family)),
      },
      {
        key: "children" as const,
        label: t("reports.families.columns.children", { defaultValue: "Children and ages" }),
        value: (family: FamilyReportRow) => formatFamilyChildren(family, {
          age: (value) => t("reports.families.child_age", {
            count: value,
            defaultValue: "{{count}} years",
          }),
          unknownAge: t("reports.families.unknown_age", { defaultValue: "age unknown" }),
        }),
      },
      {
        key: "active" as const,
        label: t("reports.families.columns.active", { defaultValue: "Active" }),
        value: (family: FamilyReportRow) =>
          family.active
            ? t("common.yes", { defaultValue: "Yes" })
            : t("common.no", { defaultValue: "No" }),
      },
      {
        key: "familySituation" as const,
        label: t("reports.families.columns.family_situation", { defaultValue: "Family situation" }),
        value: (family: FamilyReportRow) => familySituationLabel(t, family.familySituation) || "—",
      },
      {
        key: "parent1" as const,
        label: t("reports.families.columns.parent_1", { defaultValue: "Parent 1" }),
        value: (family: FamilyReportRow) => parentName(family, 1) || "—",
      },
      {
        key: "parent2" as const,
        label: t("reports.families.columns.parent_2", { defaultValue: "Parent 2" }),
        value: (family: FamilyReportRow) => parentName(family, 2) || "—",
      },
    ],
    [t],
  );

  const visibleColumns = columns.filter((column) => selectedColumns.includes(column.key));
  const filteredFamilies = useMemo(
    () => filterFamilyReportRows(families, searchQuery, statusFilter, sortEmptyFirst),
    [families, searchQuery, statusFilter, sortEmptyFirst],
  );

  const toggleColumn = (key: ColumnKey) => {
    setSelectedColumns((current) => {
      if (current.includes(key)) {
        return current.length === 1 ? current : current.filter((column) => column !== key);
      }
      return columns.map((column) => column.key).filter((column) => [...current, key].includes(column));
    });
  };

  const handleCopy = async () => {
    const headers = Object.fromEntries(columns.map((column) => [column.key, column.label]));
    const values = Object.fromEntries(columns.map((column) => [column.key, column.value]));
    try {
      await copyText(
        buildFamilyClipboardText(filteredFamilies, selectedColumns, headers, values),
      );
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    }
    window.setTimeout(() => setCopyStatus("idle"), 2500);
  };

  if (loading) return <Spinner />;
  if (error) {
    return (
      <ErrorPage
        message={error.message}
        status={error.status}
        onRetry={() => window.location.reload()}
      />
    );
  }

  const statusOptions: Array<{ key: FamilyStatusFilter; label: string }> = [
    { key: "all", label: t("family.filter.all", { defaultValue: "All families" }) },
    { key: "active", label: t("reports.families.filters.active", { defaultValue: "Active families" }) },
    { key: "inactive", label: t("reports.families.filters.inactive", { defaultValue: "Inactive families" }) },
    {
      key: "formerSchoolChildren",
      label: t("reports.families.filters.former_school_children", {
        defaultValue: "Children no longer at school",
      }),
    },
    {
      key: "multipleChildren",
      label: t("reports.families.filters.multiple_children", {
        defaultValue: "More than one child",
      }),
    },
    { key: "noChildren", label: t("family.filter.no_children", { defaultValue: "Without children" }) },
    { key: "noParents", label: t("family.filter.no_parents", { defaultValue: "Without parents" }) },
    { key: "empty", label: t("family.filter.empty", { defaultValue: "Empty families" }) },
  ];

  return (
    <LdsOverviewPage
      className="family-report-page"
      styles={(
        <style>{`
          .family-report-print-header { display: none; }
          @media print {
            @page { size: A4 landscape; margin: 12mm; }
            .family-report-no-print { display: none !important; }
            .family-report-page { padding: 0 !important; }
            .family-report-page > div { max-width: none !important; }
            .family-report-page > div > div {
              border: 0 !important;
              box-shadow: none !important;
              padding: 0 !important;
            }
            .family-report-print-header { display: block !important; }
            .family-report-table-card { border: 0 !important; box-shadow: none !important; }
            .family-report-table-scroll { overflow: visible !important; }
            .family-report-table { font-size: 10pt !important; }
            .family-report-table tr { break-inside: avoid; page-break-inside: avoid; }
          }
        `}</style>
      )}
    >
      <div className="family-report-no-print">
        <LdsOverviewHero
          icon={FaUsers}
          eyebrow={t("reports.title", { defaultValue: "Reports" })}
          title={t("reports.families.title", { defaultValue: "Family lists" })}
          description={t("reports.families.description", {
            defaultValue: "Create, filter, print and copy practical family lists.",
          })}
        />
      </div>

      <section
        ref={controlsRef}
        className="family-report-no-print relative rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/80 sm:p-5"
      >
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <label className="relative min-w-0 flex-1">
            <span className="sr-only">
              {t("reports.families.search", { defaultValue: "Search families or parents" })}
            </span>
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t("reports.families.search", { defaultValue: "Search families or parents" })}
              className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-950 outline-none transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-950/50 dark:text-white dark:focus:border-indigo-400 dark:focus:ring-indigo-400/10"
            />
          </label>

          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setFilterOpen((open) => !open);
                setColumnsOpen(false);
              }}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 xl:w-auto"
            >
              <FaFilter />
              {statusOptions.find((option) => option.key === statusFilter)?.label}
            </button>
            {filterOpen && (
              <div className="absolute right-0 z-40 mt-2 w-[min(22rem,calc(100vw-2rem))] rounded-[22px] border border-slate-200 bg-white p-4 shadow-xl dark:border-white/10 dark:bg-slate-900">
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setStatusFilter(option.key)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                        statusFilter === option.key
                          ? "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-400/40 dark:bg-indigo-400/10 dark:text-indigo-200"
                          : "border-slate-200 text-slate-600 dark:border-white/10 dark:text-slate-300"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <label className="mt-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
                  <input
                    type="checkbox"
                    checked={sortEmptyFirst}
                    onChange={(event) => setSortEmptyFirst(event.target.checked)}
                    className="h-4 w-4 accent-indigo-600"
                  />
                  {t("family.overview.sort_empty_first", { defaultValue: "Empty families first" })}
                </label>
              </div>
            )}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setColumnsOpen((open) => !open);
                setFilterOpen(false);
              }}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 xl:w-auto"
            >
              <FaColumns />
              {t("reports.families.columns.choose", { defaultValue: "Columns" })}
              <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-200">
                {selectedColumns.length}
              </span>
            </button>
            {columnsOpen && (
              <div className="absolute right-0 z-40 mt-2 w-64 rounded-[22px] border border-slate-200 bg-white p-3 shadow-xl dark:border-white/10 dark:bg-slate-900">
                {columns.map((column) => {
                  const checked = selectedColumns.includes(column.key);
                  return (
                    <label key={column.key} className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/5">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={checked && selectedColumns.length === 1}
                        onChange={() => toggleColumn(column.key)}
                        className="h-4 w-4 accent-indigo-600"
                      />
                      {column.label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 text-sm font-bold text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200"
          >
            {copyStatus === "copied" ? <FaCheck /> : <FaCopy />}
            {copyStatus === "copied"
              ? t("reports.families.copied", { defaultValue: "Copied" })
              : copyStatus === "error"
                ? t("reports.families.copy_failed", { defaultValue: "Copy failed" })
                : t("reports.families.copy", { defaultValue: "Copy" })}
          </button>

          <button
            type="button"
            onClick={() => printPage()}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 text-sm font-bold text-white shadow-lg shadow-indigo-900/15 transition hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
          >
            <FaPrint />
            {t("reports.families.print", { defaultValue: "Print" })}
          </button>
        </div>
      </section>

      <header className="family-report-print-header mb-5 border-b-2 border-slate-900 pb-3">
        <h1 className="text-2xl font-bold">
          {t("reports.families.title", { defaultValue: "Family lists" })}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {t("reports.families.print_summary", {
            count: filteredFamilies.length,
            defaultValue: "{{count}} families",
          })}
        </p>
      </header>

      <LdsTableCard className="family-report-table-card overflow-hidden">
        <div className="family-report-no-print flex flex-col gap-2 border-b border-slate-200 px-5 py-4 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              {t("reports.families.overview", { defaultValue: "Family overview" })}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t("common.result_count", {
                count: filteredFamilies.length,
                defaultValue: "{{count}} results",
              })}
            </p>
          </div>
          {(searchQuery || statusFilter !== "all" || sortEmptyFirst) && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("all");
                setSortEmptyFirst(false);
              }}
              className="text-sm font-bold text-indigo-700 hover:text-indigo-900 dark:text-indigo-300"
            >
              {t("common.clear_filters", { defaultValue: "Clear filters" })}
            </button>
          )}
        </div>

        <LdsTableScroll className="family-report-table-scroll">
          <LdsTable className="family-report-table min-w-[680px]">
            <LdsTableHead>
              <LdsTableRow>
                {visibleColumns.map((column) => (
                  <LdsTableHeadCell key={column.key}>{column.label}</LdsTableHeadCell>
                ))}
                <LdsTableHeadCell className="family-report-no-print text-right">
                  {t("reports.families.columns.actions", { defaultValue: "Actions" })}
                </LdsTableHeadCell>
              </LdsTableRow>
            </LdsTableHead>
            <LdsTableBody>
              {filteredFamilies.length > 0 ? (
                filteredFamilies.map((family) => (
                  <LdsTableRow key={family.id}>
                    {visibleColumns.map((column) => (
                      <LdsTableCell key={column.key} className={column.key === "familyName" ? "font-bold text-slate-950 dark:text-white" : undefined}>
                        {column.key === "active" ? (
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${
                            family.active
                              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200"
                              : "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300"
                          }`}>
                            {column.value(family)}
                          </span>
                        ) : column.value(family)}
                      </LdsTableCell>
                    ))}
                    <LdsTableCell className="family-report-no-print text-right">
                      <button
                        type="button"
                        onClick={() => navigate(`/family/${family.id}/details`)}
                        className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 transition hover:bg-indigo-100 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20 dark:focus:ring-indigo-400/10"
                        aria-label={t("reports.families.inspect_family_label", {
                          family: family.familyName || "",
                          defaultValue: "Inspect family {{family}}",
                        })}
                      >
                        <FaFolderOpen aria-hidden="true" />
                        {t("reports.families.inspect_family", { defaultValue: "View family" })}
                      </button>
                    </LdsTableCell>
                  </LdsTableRow>
                ))
              ) : (
                <LdsTableRow>
                  <LdsTableCell colSpan={visibleColumns.length + 1} className="py-12 text-center text-slate-500">
                    {t("reports.families.no_results", { defaultValue: "No families match these filters." })}
                  </LdsTableCell>
                </LdsTableRow>
              )}
            </LdsTableBody>
          </LdsTable>
        </LdsTableScroll>
      </LdsTableCard>
    </LdsOverviewPage>
  );
}
